import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { ArrowLeft, Save, ChevronDown, ChevronRight, Upload, Plus, Trash2, Ticket } from "lucide-react";
import { OrderDetailDialog } from "@/components/OrderDetailDialog";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const GENDERS = [{ value: "decline", label: "Decline to State" }, { value: "male", label: "Male" }, { value: "female", label: "Female" }, { value: "other", label: "Other" }];
const OPT_OPTIONS = [{ value: "opt_out", label: "Opt Out" }, { value: "opt_in", label: "Opt In" }];

export default function CustomerDetailPage() {
  const { customerId } = useParams();
  const navigate = useNavigate();
  const isNew = customerId === "new";
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [expanded, setExpanded] = useState({ basic: true, other: false, marketing: false, addresses: false, house: false, history: false });
  const [customerGroups, setCustomerGroups] = useState([]);
  const [orderHistory, setOrderHistory] = useState([]);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [loyaltyBalance, setLoyaltyBalance] = useState({ loyalty_points: 0, loyalty_lifetime_points: 0 });
  const [loyaltyHistory, setLoyaltyHistory] = useState([]);

  const [form, setForm] = useState({
    customer_type: "individual", title: "", first_name: "", last_name: "",
    phone: "", email: "", birthday: "", company_name: "", customer_group_id: "",
    ref_code: "", rewards_card: "", notes: "", gender: "decline",
    photo_url: "", license_number: "", is_active: true,
    marketing_email: "opt_out", marketing_phone: "opt_out", marketing_mail: "opt_out",
    addresses: [], house_account_enabled: false, house_account_balance: 0, house_account_max_limit: 0,
  });

  const fetchCustomer = useCallback(async () => {
    try {
      const grpRes = await api.get("/customer-groups");
      setCustomerGroups(grpRes.data);
    } catch { /* silent */ }
    if (isNew) { setLoading(false); return; }
    try {
      const [res, ordRes] = await Promise.all([
        api.get(`/customers/${customerId}`),
        api.get(`/customers/${customerId}/orders`),
      ]);
      const c = res.data;
      setOrderHistory(ordRes.data);
      // Fetch loyalty data
      try {
        const [balRes, histRes] = await Promise.all([
          api.get(`/loyalty/balance/${customerId}`),
          api.get(`/loyalty/history/${customerId}`),
        ]);
        setLoyaltyBalance(balRes.data);
        setLoyaltyHistory(histRes.data);
      } catch { /* loyalty may not exist yet */ }
      setForm({
        customer_type: c.customer_type || "individual", title: c.title || "",
        first_name: c.first_name || "", last_name: c.last_name || "",
        phone: c.phone || "", email: c.email || "", birthday: c.birthday || "",
        company_name: c.company_name || "", customer_group_id: c.customer_group_id || c.customer_group || "",
        ref_code: c.ref_code || "", rewards_card: c.rewards_card || "",
        notes: c.notes || "", gender: c.gender || "decline",
        photo_url: c.photo_url || "", license_number: c.license_number || "",
        is_active: c.is_active !== false,
        marketing_email: c.marketing_email || "opt_out",
        marketing_phone: c.marketing_phone || "opt_out",
        marketing_mail: c.marketing_mail || "opt_out",
        addresses: c.addresses || [],
        house_account_enabled: c.house_account_enabled || false,
        house_account_balance: c.house_account_balance || 0,
        house_account_max_limit: c.house_account_max_limit || 0,
      });
    } catch { toast.error("Failed to load customer"); navigate("/dashboard/customers"); }
    finally { setLoading(false); }
  }, [customerId, isNew, navigate]);

  useEffect(() => { fetchCustomer(); }, [fetchCustomer]);

  const update = (key, value) => setForm((p) => ({ ...p, [key]: value }));
  const toggleSection = (s) => setExpanded((p) => ({ ...p, [s]: !p[s] }));

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Please select an image"); return; }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await api.post("/upload/image", formData, { headers: { "Content-Type": "multipart/form-data" } });
      update("photo_url", `${BACKEND_URL}/api/files/${res.data.storage_path}`);
      toast.success("Photo uploaded!");
    } catch { toast.error("Upload failed"); }
    finally { setUploading(false); }
  };

  const addAddress = () => {
    setForm((p) => ({ ...p, addresses: [...p.addresses, { street: "", city: "", state: "", zip: "", country: "", type: "home" }] }));
  };
  const updateAddress = (idx, key, value) => {
    setForm((p) => {
      const addrs = [...p.addresses];
      addrs[idx] = { ...addrs[idx], [key]: value };
      return { ...p, addresses: addrs };
    });
  };
  const removeAddress = (idx) => setForm((p) => ({ ...p, addresses: p.addresses.filter((_, i) => i !== idx) }));

  const handleSave = async () => {
    if (!form.first_name.trim() && !form.company_name.trim()) { toast.error("First name or company name required"); return; }
    setSaving(true);
    try {
      if (isNew) {
        const res = await api.post("/customers", form);
        toast.success("Customer created");
        navigate(`/dashboard/customers/${res.data.customer_id}`, { replace: true });
      } else {
        await api.put(`/customers/${customerId}`, form);
        toast.success("Customer saved");
      }
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to save"); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="p-8 flex justify-center"><div className="animate-spin rounded-full h-8 w-8 border-2 border-[var(--brand-primary)] border-t-transparent" /></div>;

  return (
    <div className="min-h-screen bg-slate-50" data-testid="customer-detail-page">
      {/* Top Bar */}
      <div className="bg-[var(--brand-primary)] text-white px-4 py-3 flex items-center justify-between" data-testid="customer-detail-header">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" className="text-white hover:bg-white/20" onClick={() => navigate("/dashboard/customers")} data-testid="back-to-customers-btn">
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <span className="text-sm font-semibold">{isNew ? "New Customer" : `Edit Customer: ${form.first_name} ${form.last_name} ${form.phone}`}</span>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="ghost" className="text-white hover:bg-white/20" onClick={() => navigate("/dashboard/customers")}>Back to Customers</Button>
          <Button onClick={handleSave} disabled={saving} className="bg-white text-[var(--brand-primary)] hover:bg-white/90 font-semibold" data-testid="save-customer-btn">
            <Save className="h-4 w-4 mr-1" /> {saving ? "Saving..." : "Save"}
          </Button>
        </div>
      </div>

      <div className="flex max-w-7xl mx-auto">
        {/* Left Sidebar */}
        <div className="w-64 bg-white border-r border-slate-200 min-h-[calc(100vh-52px)] p-4 flex-shrink-0" data-testid="customer-sections-nav">
          <p className="text-xs uppercase tracking-wider text-slate-400 font-semibold mb-3">Select Desired Settings</p>
          {[
            { id: "basic", label: "Basic Customer Details", sub: ["Type", "Title", "First Name", "Last Name", "Phone", "Email", "Birthday", "Company", "Customer Group"] },
            { id: "other", label: "Other Customer Details", sub: ["Ref. Code", "Rewards Card", "Notes", "Gender", "Photo", "License", "Active"] },
            { id: "marketing", label: "Marketing Opt In/Out", sub: ["Email", "Phone/SMS", "Mail"] },
            { id: "addresses", label: "Address(es)", sub: [`${form.addresses.length} addresses`] },
            { id: "house", label: "House Account", sub: form.house_account_enabled ? ["Enabled", `Balance: $${form.house_account_balance.toFixed(2)}`] : ["Disabled"] },
            ...(!isNew ? [{ id: "loyalty", label: "Loyalty Points", sub: [`${loyaltyBalance.loyalty_points} pts`, `Lifetime: ${loyaltyBalance.loyalty_lifetime_points}`] }] : []),
            ...(!isNew ? [{ id: "history", label: "Order History", sub: [`${orderHistory.length} orders`] }] : []),
          ].map((section) => (
            <div key={section.id} className="mb-2">
              <button
                onClick={() => toggleSection(section.id)}
                className={`flex items-center gap-2 w-full text-left py-1.5 px-2 rounded text-sm font-medium ${expanded[section.id] ? "text-[var(--brand-primary)] bg-blue-50" : "text-slate-600 hover:bg-slate-50"}`}
                data-testid={`section-toggle-${section.id}`}
              >
                {expanded[section.id] ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
                <Checkbox checked={expanded[section.id]} className="border-slate-300" />
                <span>{section.label}</span>
              </button>
              {expanded[section.id] && section.sub && (
                <div className="pl-10 space-y-0.5 mt-1">
                  {section.sub.map((s) => <p key={s} className="text-xs text-[var(--brand-primary)]">{s}</p>)}
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Main Content */}
        <div className="flex-1 p-6 space-y-6" data-testid="customer-detail-content">
          {/* Basic Customer Details */}
          {expanded.basic && (
            <div className="bg-white border border-slate-200 rounded-lg" data-testid="basic-customer-section">
              <div className="border-b border-slate-200 px-6 py-3">
                <h2 className="font-heading text-base font-semibold text-slate-900">Basic Customer Details</h2>
              </div>
              <div className="px-6 py-4 space-y-4">
                <div className="grid grid-cols-[160px_1fr] items-center gap-4">
                  <Label className="text-sm text-slate-600 text-right">Type *</Label>
                  <Select value={form.customer_type} onValueChange={(v) => update("customer_type", v)}>
                    <SelectTrigger className="max-w-xs" data-testid="customer-type-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="individual">Individual</SelectItem>
                      <SelectItem value="company">Company</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-[160px_1fr] items-center gap-4">
                  <Label className="text-sm text-slate-600 text-right">Title</Label>
                  <Input value={form.title} onChange={(e) => update("title", e.target.value)} className="max-w-xs" data-testid="customer-title-input" />
                </div>
                <div className="grid grid-cols-[160px_1fr] items-center gap-4">
                  <Label className="text-sm text-slate-600 text-right">First Name</Label>
                  <Input value={form.first_name} onChange={(e) => update("first_name", e.target.value)} data-testid="customer-first-name" />
                </div>
                <div className="grid grid-cols-[160px_1fr] items-center gap-4">
                  <Label className="text-sm text-slate-600 text-right">Last Name</Label>
                  <Input value={form.last_name} onChange={(e) => update("last_name", e.target.value)} data-testid="customer-last-name" />
                </div>
                <div className="grid grid-cols-[160px_1fr] items-center gap-4">
                  <Label className="text-sm text-slate-600 text-right">Phone Number</Label>
                  <Input value={form.phone} onChange={(e) => update("phone", e.target.value)} data-testid="customer-phone" />
                </div>
                <div className="grid grid-cols-[160px_1fr] items-center gap-4">
                  <Label className="text-sm text-slate-600 text-right">Email</Label>
                  <Input type="email" value={form.email} onChange={(e) => update("email", e.target.value)} data-testid="customer-email" />
                </div>
                <div className="grid grid-cols-[160px_1fr] items-center gap-4">
                  <Label className="text-sm text-slate-600 text-right">Birthday</Label>
                  <Input type="date" value={form.birthday} onChange={(e) => update("birthday", e.target.value)} className="max-w-xs" data-testid="customer-birthday" />
                </div>
                <div className="grid grid-cols-[160px_1fr] items-center gap-4">
                  <Label className="text-sm text-slate-600 text-right">Company Name</Label>
                  <Input value={form.company_name} onChange={(e) => update("company_name", e.target.value)} data-testid="customer-company" />
                </div>
                <div className="grid grid-cols-[160px_1fr] items-center gap-4">
                  <Label className="text-sm text-slate-600 text-right">Customer Group</Label>
                  <Select value={form.customer_group_id || "none"} onValueChange={(v) => update("customer_group_id", v === "none" ? "" : v)}>
                    <SelectTrigger className="max-w-xs" data-testid="customer-group"><SelectValue placeholder="Select group" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">None</SelectItem>
                      {customerGroups.filter((g) => g.is_active).map((g) => (
                        <SelectItem key={g.customer_group_id} value={g.customer_group_id}>
                          {g.name} {g.discount_percent > 0 ? `(${g.discount_percent}% discount)` : ""}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          )}

          {/* Other Customer Details */}
          {expanded.other && (
            <div className="bg-white border border-slate-200 rounded-lg" data-testid="other-customer-section">
              <div className="border-b border-slate-200 px-6 py-3">
                <h2 className="font-heading text-base font-semibold text-slate-900">Other Customer Details</h2>
              </div>
              <div className="px-6 py-4 space-y-4">
                <div className="grid grid-cols-[160px_1fr] items-center gap-4">
                  <Label className="text-sm text-slate-600 text-right">Ref. Code</Label>
                  <Input value={form.ref_code} onChange={(e) => update("ref_code", e.target.value)} data-testid="customer-ref-code" />
                </div>
                <div className="grid grid-cols-[160px_1fr] items-center gap-4">
                  <Label className="text-sm text-slate-600 text-right">Rewards Card</Label>
                  <Input value={form.rewards_card} onChange={(e) => update("rewards_card", e.target.value)} placeholder="Start typing rewards card number" data-testid="customer-rewards" />
                </div>
                <div className="grid grid-cols-[160px_1fr] items-start gap-4">
                  <Label className="text-sm text-slate-600 text-right pt-2">Customer Notes</Label>
                  <textarea value={form.notes} onChange={(e) => update("notes", e.target.value)} className="flex min-h-[80px] w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring" data-testid="customer-notes" />
                </div>
                <div className="grid grid-cols-[160px_1fr] items-center gap-4">
                  <Label className="text-sm text-slate-600 text-right">Gender</Label>
                  <Select value={form.gender} onValueChange={(v) => update("gender", v)}>
                    <SelectTrigger className="max-w-xs" data-testid="customer-gender"><SelectValue /></SelectTrigger>
                    <SelectContent>{GENDERS.map((g) => <SelectItem key={g.value} value={g.value}>{g.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-[160px_1fr] items-start gap-4">
                  <Label className="text-sm text-slate-600 text-right pt-2">Customer Photo</Label>
                  <div className="space-y-2">
                    <div className="flex gap-2 items-center">
                      <label className="cursor-pointer">
                        <input type="file" accept="image/*" onChange={handlePhotoUpload} className="hidden" data-testid="customer-photo-upload" />
                        <div className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded border border-[var(--brand-primary)] text-xs font-bold text-[var(--brand-primary)] hover:bg-blue-50 ${uploading ? "opacity-50" : ""}`}>
                          <Upload className="h-3.5 w-3.5" />{uploading ? "UPLOADING..." : "BROWSE & UPLOAD"}
                        </div>
                      </label>
                      <span className="text-xs text-slate-400">or</span>
                      <span className="text-xs text-slate-500 font-medium">USE URL</span>
                    </div>
                    <Input value={form.photo_url} onChange={(e) => update("photo_url", e.target.value)} placeholder="https://..." data-testid="customer-photo-url" />
                    {form.photo_url && <img src={form.photo_url} alt="Preview" className="h-16 w-16 rounded-full object-cover border border-slate-200" />}
                  </div>
                </div>
                <div className="grid grid-cols-[160px_1fr] items-center gap-4">
                  <Label className="text-sm text-slate-600 text-right">License Number</Label>
                  <Input value={form.license_number} onChange={(e) => update("license_number", e.target.value)} data-testid="customer-license" />
                </div>
                <div className="grid grid-cols-[160px_1fr] items-center gap-4">
                  <Label className="text-sm text-slate-600 text-right">Active</Label>
                  <Checkbox checked={form.is_active} onCheckedChange={(v) => update("is_active", v)} data-testid="customer-active" />
                </div>
              </div>
            </div>
          )}

          {/* Marketing Opt In/Out */}
          {expanded.marketing && (
            <div className="bg-white border border-slate-200 rounded-lg" data-testid="marketing-section">
              <div className="border-b border-slate-200 px-6 py-3">
                <h2 className="font-heading text-base font-semibold text-slate-900">Marketing Opt In/Out</h2>
              </div>
              <div className="px-6 py-4 space-y-4">
                {[
                  { key: "marketing_email", label: "Email" },
                  { key: "marketing_phone", label: "Phone/SMS" },
                  { key: "marketing_mail", label: "Mail" },
                ].map((opt) => (
                  <div key={opt.key} className="grid grid-cols-[160px_1fr] items-center gap-4">
                    <Label className="text-sm text-slate-600 text-right">{opt.label}</Label>
                    <Select value={form[opt.key]} onValueChange={(v) => update(opt.key, v)}>
                      <SelectTrigger className="max-w-xs" data-testid={`customer-${opt.key}`}><SelectValue /></SelectTrigger>
                      <SelectContent>{OPT_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Addresses */}
          {expanded.addresses && (
            <div className="bg-white border border-slate-200 rounded-lg" data-testid="addresses-section">
              <div className="border-b border-slate-200 px-6 py-3 flex items-center justify-between">
                <h2 className="font-heading text-base font-semibold text-slate-900">Address(es)</h2>
                <Badge variant="secondary" className="text-xs">{form.addresses.length}</Badge>
              </div>
              <div className="px-6 py-4 space-y-4">
                {form.addresses.map((addr, idx) => (
                  <div key={idx} className="border border-slate-200 rounded-lg p-4 relative" data-testid={`address-${idx}`}>
                    <button onClick={() => removeAddress(idx)} className="absolute top-3 right-3 p-1 rounded hover:bg-red-50 text-red-500" data-testid={`remove-address-${idx}`}><Trash2 className="h-4 w-4" /></button>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="text-xs text-slate-500">Street</Label>
                        <Input value={addr.street} onChange={(e) => updateAddress(idx, "street", e.target.value)} />
                      </div>
                      <div>
                        <Label className="text-xs text-slate-500">City</Label>
                        <Input value={addr.city} onChange={(e) => updateAddress(idx, "city", e.target.value)} />
                      </div>
                      <div>
                        <Label className="text-xs text-slate-500">State</Label>
                        <Input value={addr.state} onChange={(e) => updateAddress(idx, "state", e.target.value)} />
                      </div>
                      <div>
                        <Label className="text-xs text-slate-500">ZIP</Label>
                        <Input value={addr.zip} onChange={(e) => updateAddress(idx, "zip", e.target.value)} />
                      </div>
                    </div>
                  </div>
                ))}
                <button onClick={addAddress} className="text-sm text-[var(--brand-primary)] hover:underline font-medium" data-testid="add-address-btn">+ Add Address</button>
              </div>
            </div>
          )}

          {/* House Account */}
          {expanded.house && (
            <div className="bg-white border border-slate-200 rounded-lg" data-testid="house-account-section">
              <div className="border-b border-slate-200 px-6 py-3">
                <h2 className="font-heading text-base font-semibold text-slate-900">House Account</h2>
              </div>
              <div className="px-6 py-4 space-y-4">
                <div className="grid grid-cols-[160px_1fr] items-center gap-4">
                  <Label className="text-sm text-slate-600 text-right">Enabled</Label>
                  <Checkbox checked={form.house_account_enabled} onCheckedChange={(v) => update("house_account_enabled", v)} data-testid="house-enabled" />
                </div>
                {form.house_account_enabled && (
                  <>
                    <div className="grid grid-cols-[160px_1fr] items-center gap-4">
                      <Label className="text-sm text-slate-600 text-right">Balance *</Label>
                      <Input type="number" step="0.01" value={form.house_account_balance} onChange={(e) => update("house_account_balance", parseFloat(e.target.value) || 0)} className="max-w-xs" data-testid="house-balance" />
                    </div>
                    <div className="grid grid-cols-[160px_1fr] items-center gap-4">
                      <Label className="text-sm text-slate-600 text-right">Max Limit</Label>
                      <Input type="number" step="0.01" value={form.house_account_max_limit} onChange={(e) => update("house_account_max_limit", parseFloat(e.target.value) || 0)} className="max-w-xs" data-testid="house-max-limit" />
                    </div>
                  </>
                )}
              </div>
            </div>
          )}

          {/* Loyalty Points */}
          {expanded.loyalty && !isNew && (
            <div className="bg-white border border-slate-200 rounded-lg" data-testid="loyalty-section">
              <div className="border-b border-slate-200 px-6 py-3 flex items-center justify-between">
                <h2 className="font-heading text-base font-semibold text-slate-900">Loyalty Points</h2>
                <Badge variant="secondary" className="text-xs font-mono">{loyaltyBalance.loyalty_points} pts</Badge>
              </div>
              <div className="px-6 py-4 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-center">
                    <p className="text-2xl font-black text-blue-700">{loyaltyBalance.loyalty_points}</p>
                    <p className="text-xs text-blue-500">Current Balance</p>
                  </div>
                  <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-center">
                    <p className="text-2xl font-black text-slate-700">{loyaltyBalance.loyalty_lifetime_points}</p>
                    <p className="text-xs text-slate-500">Lifetime Points</p>
                  </div>
                </div>
                {loyaltyHistory.length > 0 && (
                  <div>
                    <p className="text-xs font-semibold text-slate-500 uppercase mb-2">Recent Activity</p>
                    <div className="space-y-1 max-h-[200px] overflow-y-auto">
                      {loyaltyHistory.slice(0, 20).map((tx) => (
                        <div key={tx.transaction_id} className="flex items-center justify-between py-1.5 px-2 rounded bg-slate-50 text-xs">
                          <div>
                            <span className={tx.type === "earn" ? "text-green-700 font-semibold" : "text-red-600 font-semibold"}>
                              {tx.type === "earn" ? "+" : ""}{tx.points} pts
                            </span>
                            <span className="text-slate-500 ml-2">{tx.description}</span>
                          </div>
                          <span className="text-slate-400">{new Date(tx.created_at).toLocaleDateString()}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Order History */}
          {expanded.history && !isNew && (
            <div className="bg-white border border-slate-200 rounded-lg" data-testid="order-history-section">
              <div className="border-b border-slate-200 px-6 py-3 flex items-center justify-between">
                <h2 className="font-heading text-base font-semibold text-slate-900">Order History</h2>
                <Badge variant="secondary" className="text-xs">{orderHistory.length} orders</Badge>
              </div>
              <div className="px-6 py-4">
                {orderHistory.length > 0 ? (
                  <div className="space-y-2">
                    {orderHistory.map((order) => (
                      <div
                        key={order.order_id}
                        className="flex items-center gap-4 p-3 rounded-lg border border-slate-100 hover:bg-blue-50/50 cursor-pointer transition-colors"
                        onClick={() => setSelectedOrder(order)}
                        data-testid={`order-hist-${order.order_id}`}
                      >
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <Ticket className="h-3.5 w-3.5 text-[var(--brand-primary)]" />
                            <span className="text-sm font-mono font-bold text-slate-900">{order.ticket_number || order.order_id?.slice(-8)}</span>
                            {order.dining_type_name && <Badge variant="outline" className="text-[10px]">{order.dining_type_name}</Badge>}
                            <Badge variant={order.status === "completed" ? "default" : "secondary"} className="text-xs">{order.status}</Badge>
                            <Badge variant="outline" className="text-xs capitalize">{order.payment_method}</Badge>
                            {order.group_discount_applied > 0 && <Badge className="text-xs bg-emerald-100 text-emerald-700">-{order.group_discount_applied}%</Badge>}
                          </div>
                          <p className="text-xs text-slate-400 mt-1">
                            {order.created_at ? new Date(order.created_at).toLocaleString() : "-"} | {order.items?.length || 0} items | by {order.employee_name || "-"}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-sm font-bold text-slate-900">${(order.total || 0).toFixed(2)}</p>
                          {order.discount > 0 && <p className="text-xs text-emerald-600">Saved ${order.discount.toFixed(2)}</p>}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-slate-400 text-center py-8">No orders yet for this customer.</p>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Order Detail Dialog */}
      <OrderDetailDialog order={selectedOrder} open={!!selectedOrder} onOpenChange={(open) => { if (!open) setSelectedOrder(null); }} />
    </div>
  );
}
